import React, { useState } from "react";
import { Button } from "../../../../ui/components/Button";
import { getZohoConfigApi } from "../../data/api/authApi";

interface ZohoAuthCardProps {
  onAuthInitiated?: () => void;
}

interface ZohoConfig {
  clientId: string;
  redirectUri: string;
  authUrl: string;
  tokenUrl: string;
  apiUrl: string;
  scope: string;
  responseType: string;
  accessType: string;
  prompt: string;
}

export const ZohoAuthCard: React.FC<ZohoAuthCardProps> = ({
  onAuthInitiated,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleZohoAuth = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch Zoho config from backend
      const response = await getZohoConfigApi();

      if (!response.success || !response.data) {
        throw new Error("Failed to load Zoho configuration");
      }

      const zohoConfig: ZohoConfig = response.data;
      // Build the auth URL
      const authUrl = new URL(zohoConfig.authUrl);
      console.log("zohoConfig.redirectUri", zohoConfig.redirectUri);
      authUrl.searchParams.append("client_id", zohoConfig.clientId);
      authUrl.searchParams.append("response_type", zohoConfig.responseType);
      authUrl.searchParams.append("redirect_uri", zohoConfig.redirectUri);
      authUrl.searchParams.append("scope", zohoConfig.scope);
      authUrl.searchParams.append("access_type", zohoConfig.accessType);
      authUrl.searchParams.append("prompt", zohoConfig.prompt);

      onAuthInitiated?.();
      window.location.href = authUrl.toString();
    } catch (err) {
      console.error("Zoho auth error:", err);
      setError("Failed to initiate Zoho authentication. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-start justify-center gap-8">
      <div className="flex w-full flex-col items-start gap-1">
        <span className="w-full text-heading-2 font-heading-2 text-default-font">
          Zoho Authentication Required
        </span>
        <span className="text-body font-body text-subtext-color">
          To access Zoho Books features, you need to authenticate with your Zoho
          account.
        </span>
      </div>

      {error && (
        <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-sm font-medium text-red-800">{error}</span>
        </div>
      )}

      <div className="flex w-full flex-col items-start justify-center gap-4">
        <Button
          className="h-8 w-full flex-none"
          onClick={handleZohoAuth}
          disabled={isLoading}
        >
          {isLoading ? "Connecting..." : "Connect to Zoho"}
        </Button>

        <div className="flex w-full flex-col items-start gap-4 p-4 bg-neutral-50 border border-neutral-200 rounded-lg">
          <span className="text-sm font-medium text-default-font">
            What this does:
          </span>
          <ul className="flex flex-col items-start gap-2 text-sm text-subtext-color">
            <li>• Connects your account to Zoho Books</li>
            <li>• Allows access to invoices, bills, and other data</li>
            <li>• Secure OAuth authentication</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
